import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IDeliveryConditionResponse } from '../models/delivery-condition.model';
import { DeliveryConditionApiService } from '../services/delivery-condition-api.service';
import {
  CreateDeliveryCondition,
  DeleteDeliveryCondition,
  LoadAllDeliveryConditions,
  LoadDeliveryConditionById,
  LoadDeliveryConditions,
  UpdateDeliveryCondition,
  ClearCurrentDeliveryCondition,
} from './delivery-condition.actions';

export interface DeliveryConditionStateModel {
  deliveryConditionsLoaded: boolean;
  deliveryConditions: IDeliveryConditionResponse[];
  allDeliveryConditions: IDeliveryConditionResponse[];
  currentDeliveryCondition: IDeliveryConditionResponse | undefined;
  deliveryConditionsTotalPages: number;
}

const DELIVERY_CONDITION_STATE_TOKEN = new StateToken<DeliveryConditionStateModel>(
  'deliveryCondition'
);

@State<DeliveryConditionStateModel>({
  name: DELIVERY_CONDITION_STATE_TOKEN,
  defaults: {
    deliveryConditionsLoaded: false,
    deliveryConditions: [],
    allDeliveryConditions: [],
    deliveryConditionsTotalPages: 0,
    currentDeliveryCondition: undefined,
  },
})
@Injectable()
export class DeliveryConditionState {
  constructor(private service: DeliveryConditionApiService) {}

  @Action(LoadDeliveryConditions)
  loadDeliveryConditions(
    { getState, setState }: StateContext<DeliveryConditionStateModel>,
    { filter }: LoadDeliveryConditions
  ) {
    return this.service.loadDeliveryConditions(filter).pipe(
      tap((response: ApiResponse<IDeliveryConditionResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          deliveryConditionsLoaded: true,
          deliveryConditions: response.data,
          deliveryConditionsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllDeliveryConditions, { cancelUncompleted: true })
  loadAllDeliveryConditions(
    ctx: StateContext<DeliveryConditionStateModel>,
    { departmentId }: LoadAllDeliveryConditions
  ) {
    return this.service.loadAllDeliveryConditions(departmentId).pipe(
      tap((allDeliveryConditions: IDeliveryConditionResponse[]) => {
        ctx.setState(patch({ allDeliveryConditions }));
      })
    );
  }

  @Action(LoadDeliveryConditionById, { cancelUncompleted: true })
  loadDeliveryConditionById(
    { setState }: StateContext<DeliveryConditionStateModel>,
    { id }: LoadDeliveryConditionById
  ) {
    return this.service.loadDeliveryConditionById(id).pipe(
      tap((deliveryCondition: IDeliveryConditionResponse) => {
        setState(
          patch({
            currentDeliveryCondition: deliveryCondition,
          })
        );
      })
    );
  }

  @Action(CreateDeliveryCondition)
  @OnSuccess({ url: AdminOrderRoutes.DeliveryConditions, message: 'deliveryConditionCreated' })
  createDeliveryCondition(
    { setState }: StateContext<DeliveryConditionStateModel>,
    { deliveryCondition }: CreateDeliveryCondition
  ) {
    return this.service.createDeliveryCondition(deliveryCondition).pipe(
      tap((response: IDeliveryConditionResponse) => {
        setState(
          patch({
            deliveryConditions: insertItem<IDeliveryConditionResponse>(response),
          })
        );
      })
    );
  }

  @Action(UpdateDeliveryCondition)
  @OnSuccess({ url: AdminOrderRoutes.DeliveryConditions, message: 'deliveryConditionUpdated' })
  updateDeliveryCondition(
    { setState }: StateContext<DeliveryConditionStateModel>,
    { deliveryCondition }: UpdateDeliveryCondition
  ) {
    return this.service.updateDeliveryCondition(deliveryCondition);
  }

  @Action(DeleteDeliveryCondition, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.DeliveryConditions,
    message: 'deliveryConditionDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteDeliveryCondition(
    ctx: StateContext<DeliveryConditionStateModel>,
    { id }: DeleteDeliveryCondition
  ) {
    return this.service.deleteDeliveryCondition(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            deliveryConditions: removeItem<IDeliveryConditionResponse>(
              (dcToDelete: IDeliveryConditionResponse | undefined) => dcToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentDeliveryCondition)
  clearCurrentDeliveryCondition(ctx: StateContext<DeliveryConditionStateModel>) {
    ctx.setState(patch({ currentDeliveryCondition: undefined }));
  }

  @Selector()
  static totalPages(state: DeliveryConditionStateModel) {
    return state.deliveryConditionsTotalPages;
  }

  @Selector()
  static deliveryConditions(state: DeliveryConditionStateModel) {
    return state.deliveryConditions;
  }

  @Selector()
  static allDeliveryConditions(state: DeliveryConditionStateModel): IDeliveryConditionResponse[] {
    return state.allDeliveryConditions;
  }

  @Selector()
  static getCurrentDeliveryCondition(state: DeliveryConditionStateModel) {
    return state.currentDeliveryCondition;
  }
}
